// Stage 50: Gadfly Inspector — per-turn live view.
//
// Renders the CURRENT TURN's Gadfly bites + any swat tags the persona has
// applied. The view auto-clears when a new turn starts (a fresh
// `gadfly_bites` SSE event replaces the prior turn's snapshot wholesale).
// Mounted inside the SettingsModal Gadfly tab.
//
// IMPORTANT (TINS §Implementation Notes #4): the "View raw confusion.md"
// button is gated behind the Vite DEV flag so the operator can inspect the
// Gemini response in development without exposing rawConfusionMd in
// production builds. Even with DEV, the raw text appears only inside an
// expandable <details> — it is never rendered into the chat itself.

import { useState } from 'react';
import { useGadflyTurn } from '../hooks/useGadflyTurn';
import type { BiteKind, SwatTag } from '../api/gadfly';

const KIND_COLOR: Record<BiteKind, string> = {
  OBSERVATION: 'var(--muted)',
  QUESTION: 'var(--accent)',
  OBJECTION: 'var(--danger)',
};

const TAG_COLOR: Record<SwatTag, string> = {
  ACCEPT: 'var(--ok)',
  REJECT: 'var(--danger)',
  DEFER: 'var(--warning)',
};

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

export function GadflyInspector() {
  const turn = useGadflyTurn();
  const [rawOpen, setRawOpen] = useState(false);

  if (!turn.turnId) {
    return (
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          color: 'var(--muted)',
          fontSize: 13,
        }}
        aria-live="polite"
      >
        No current turn — start chatting (with Gadfly enabled) to see live
        bites here.
      </div>
    );
  }

  const taggedCount = turn.swatEntries.length;
  const totalCount = turn.bites.length;
  const statusLine = turn.failed
    ? `0 bites — failed: ${turn.failureReason ?? 'unknown'}`
    : `${totalCount} bite${totalCount === 1 ? '' : 's'}, ${taggedCount} tagged`;

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--warning)',
        borderRadius: 8,
        fontSize: 13,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <strong style={{ color: 'var(--warning)' }}>
          Current turn: <code>{turn.turnId.slice(0, 8)}</code>
        </strong>
        <span style={{ color: 'var(--muted)' }}>{fmtTime(turn.generatedAt)}</span>
      </div>
      <div style={{ color: 'var(--muted)', marginTop: 4 }}>{statusLine}</div>

      {turn.bites.length > 0 ? (
        <ul
          role="list"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '10px 0 0 0',
          }}
        >
          {turn.bites.map((bite) => {
            const swat = turn.swatEntries.find(
              (e) => e.biteIndex === bite.index,
            );
            return (
              <li
                key={bite.index}
                role="listitem"
                style={{
                  padding: '6px 8px',
                  marginBottom: 6,
                  background: 'var(--panel-2)',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 6,
                }}
              >
                <div>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      color: 'var(--muted)',
                      marginRight: 6,
                    }}
                  >
                    [{bite.index}]
                  </span>
                  <span
                    style={{
                      color: KIND_COLOR[bite.kind],
                      fontWeight: 600,
                      marginRight: 6,
                    }}
                  >
                    [{bite.kind}]
                  </span>
                  <span>{bite.text}</span>
                </div>
                {swat ? (
                  <div
                    style={{
                      marginTop: 4,
                      color: TAG_COLOR[swat.tag],
                      fontSize: 12,
                    }}
                    aria-label={`tagged ${swat.tag}`}
                  >
                    ▸ {swat.tag} — &ldquo;{swat.reason}&rdquo;
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: 4,
                      color: 'var(--muted)',
                      fontSize: 12,
                    }}
                  >
                    (untagged — implicit REJECT)
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {turn.stripeRatioApplied ? (
        <div
          style={{
            marginTop: 8,
            color: 'var(--muted)',
            fontSize: 12,
          }}
        >
          Stripe: {turn.stripeRatioApplied.keep}-of-
          {turn.stripeRatioApplied.keep + turn.stripeRatioApplied.cut} ·
          model: <code>{turn.modelUsed}</code> ·
          {turn.durationMs ?? 0}ms
        </div>
      ) : null}

      {import.meta.env.DEV ? (
        <details
          style={{ marginTop: 10 }}
          open={rawOpen}
          onToggle={(e) => setRawOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary
            style={{ cursor: 'pointer', fontSize: 12, color: 'var(--warning)' }}
          >
            View raw confusion.md (dev only)
          </summary>
          <pre
            style={{
              marginTop: 8,
              padding: 8,
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--fg)',
              borderRadius: 4,
              fontSize: 11,
              maxHeight: 200,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {/* The raw text is read from the bites' rawLine field as a
                proxy — the full rawConfusionMd is NOT broadcast over SSE
                (only the parsed bites are). To see the full raw, use the
                "Test bite" affordance in this tab. */}
            {turn.bites.map((b) => b.rawLine).join('\n') || '(empty)'}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
